import { calculateBoundingBoxCornerPoints } from "@nova/calculate-geospatial"
import { LineStroke, LineWidth } from "@nova/constants"
import { Coordinate, Line } from "@nova/protobuf"
import { createSelector } from "@reduxjs/toolkit"
import { BoundingBoxLayerData } from "plugins/shared/layers/BoundingBoxLayer.class"
import { RootState } from "integrations/redux/rootReducer"
import { memoize } from "nodash"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"
import { getAncestors } from "panels/content/side-panel/utils"
import { selectHoveredBoundingBoxId } from "../flights/selectors"

const root = (state: RootState) => state.project.data.lines

export const selectAllLines = (state: RootState) => root(state)
export const selectNonArchivedLines = createSelector([selectAllLines], (lines) =>
    lines.filter((line) => !line.archivedTimestamp)
)
export const selectLinesMap = createSelector([selectAllLines], (lines) => {
    return lines.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, Line>
    )
})

export const selectLineById = memoize((id: string) =>
    createSelector([selectLinesMap], (lines) => {
        return lines[id]
    })
)

export const selectVisibleLines = createSelector(
    [selectNonArchivedLines, selectAllEntitiesMap],
    (lines, entityMap) =>
        lines.filter(
            (line) =>
                line.visible &&
                !getAncestors(line.id, entityMap).find((ancestor) => !ancestor.visible)
        )
)

export const selectAllLineBoundingBoxes = createSelector([selectVisibleLines], (lines) =>
    lines.reduce((boundingBoxes, line) => {
        const allPoints: Coordinate[] = line.coordinates

        if (allPoints.length > 0) {
            const box = calculateBoundingBoxCornerPoints(allPoints)
            const polygon = box.polygon

            boundingBoxes.push({
                id: line.id,
                polygon: box.polygon,
                path: [...polygon, polygon[0]],
                area: box.area,
            })
        }
        return boundingBoxes
    }, [] as BoundingBoxLayerData[])
)

export const selectLinesToDraw = createSelector([selectVisibleLines], (lines) =>
    lines.map((line, i) => ({
        ...line,
        idx: i,
    }))
)

export const selectIsDrawingLine = createSelector([selectLinesToDraw], (lines) => {
    const activeIndex = lines.length - 1
    const activeLine = lines[activeIndex]
    const isDrawing = activeLine?.coordinates?.length > 0
    return isDrawing
})

export const selectLineColorById = memoize((id: string) =>
    createSelector([selectLineById(id)], (line) => line?.color)
)

export const selectLineOutlineById = memoize((id: string) =>
    createSelector([selectLineById(id)], (line) => line?.outline)
)

export const selectLineFillById = memoize((id: string) =>
    createSelector([selectLineById(id)], (line) => line?.fill)
)

export const selectLineStrokeById = memoize((id: string) =>
    createSelector([selectLineById(id)], (line) => line?.stroke as LineStroke | undefined)
)

export const selectLineWidthById = memoize((id: string) =>
    createSelector([selectLineById(id)], (line) => line?.width as LineWidth | undefined)
)

export const selectHoveredLineId = createSelector(
    [selectNonArchivedLines, selectHoveredBoundingBoxId],
    (lines, id) => (lines.some((line) => line.id === id) ? id : undefined)
)
